// Mirrored in applications/platform-app/components/web-analytics — change both.
/**
 * The ONLY thing that differs between the two dashboards: super-admin passes
 * an adapter over `/admin/sites/...` (admin-pool token), platform-app one over
 * `/my/sites/...` (merchant token). Everything in this folder talks to this
 * interface and nothing else.
 */
import type {
  SitesResponse, SummaryResponse, TimeseriesResponse, BreakdownResponse, FunnelResponse, RealtimeResponse,
  SitesTableResponse, QueryBody, QueryResponse, SecurityResponse, Incident, Block, BlockCreate,
  SettingsResponse, SettingsUpdate, ExportDownload, WebRange, WebDim, WebFilter,
} from './types';

export interface WebAnalyticsApi {
  sites(): Promise<SitesResponse>;
  summary(siteId: string, r: WebRange): Promise<SummaryResponse>;
  timeseries(siteId: string, r: WebRange, metric: string): Promise<TimeseriesResponse>;
  breakdown(siteId: string, r: WebRange, dim: WebDim, limit?: number): Promise<BreakdownResponse>;
  funnel(siteId: string, r: WebRange): Promise<FunnelResponse>;
  realtime(siteId: string): Promise<RealtimeResponse>;
  /** Admin only (`_all`). */
  sitesTable(r: WebRange): Promise<SitesTableResponse>;
  query(siteId: string, spec: QueryBody): Promise<QueryResponse>;
  /** `opts` re-presents the cached result (metric for timeseries, limit for breakdowns). */
  queryStatus(siteId: string, queryId: string, opts?: { metric?: string; limit?: number }): Promise<QueryResponse>;
  exportCsv(siteId: string, r: WebRange, filters: WebFilter[]): Promise<{ exportId: string }>;
  /** 200 { url } · 409 still being prepared · 404/410 expired or failed — thrown as WebAnalyticsError. */
  exportDownload(siteId: string, exportId: string): Promise<ExportDownload>;
  security(siteId: string, r: WebRange): Promise<SecurityResponse>;
  incidents(siteId: string, r: WebRange): Promise<{ incidents: Incident[] }>;
  blocks(siteId: string): Promise<{ blocks: Block[] }>;
  createBlock(siteId: string, body: BlockCreate): Promise<Block>;
  /** On `_all` the row's own `scope` is needed to find it (DELETE ...?scope=). */
  deleteBlock(siteId: string, blockId: string, scope?: string): Promise<void>;
  settings(siteId: string): Promise<SettingsResponse>;
  saveSettings(siteId: string, body: SettingsUpdate): Promise<SettingsResponse>;
}

/** Thrown by adapters for any non-2xx; `message` is the server's own message (shown verbatim). */
export class WebAnalyticsError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'WebAnalyticsError';
    this.status = status;
  }
}

export const errorStatus = (e: unknown): number => (e instanceof WebAnalyticsError ? e.status : (e as any)?.status ?? 0);
export const errorMessage = (e: unknown, fallback = 'Something went wrong — try again'): string =>
  (e instanceof Error && e.message) || fallback;

/** Shared request helper for adapters: JSON in/out, server message on error. */
export async function requestJson<T>(
  url: string, init: { method?: string; body?: unknown; token?: string; headers?: Record<string, string> } = {},
): Promise<T> {
  const res = await fetch(url, {
    method: init.method || 'GET',
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}), ...(init.token ? { Authorization: init.token } : {}) },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const text = await res.text().catch(() => '');
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { const j = JSON.parse(text); msg = j.message || j.error || msg; } catch { if (text && text.length < 300 && !text.startsWith('<')) msg = text; }
    throw new WebAnalyticsError(res.status, msg);
  }
  return (text ? JSON.parse(text) : undefined) as T;
}

/** WebRange → query string (compare=0 only when off; the server defaults it on). */
export function rangeQs(r: WebRange): string {
  const p = new URLSearchParams();
  p.set('range', r.range);
  if (r.range === 'custom') { if (r.from) p.set('from', r.from); if (r.to) p.set('to', r.to); }
  if (!r.compare) p.set('compare', '0');
  return p.toString();
}

/**
 * Builds a WebAnalyticsApi over one base (`.../admin` or `.../my`). Both apps
 * call this with their own base URL and token getter. `headers` is merged into
 * every request — platform-app MUST pass `x-tenant-id` here: `custom:tenantId`
 * is a comma-separated list and a multi-tenant merchant is refused (403)
 * without the header selecting which tenant.
 */
export function createWebAnalyticsApi(
  base: () => string,
  token: () => string | undefined,
  headers?: () => Record<string, string>,
): WebAnalyticsApi {
  const s = (siteId: string) => `${base()}/sites/${encodeURIComponent(siteId)}`;
  const get = <T>(url: string) => requestJson<T>(url, { token: token(), headers: headers?.() });
  const send = <T>(method: string, url: string, body?: unknown) => requestJson<T>(url, { method, body, token: token(), headers: headers?.() });
  const rangeBody = (r: WebRange) => (r.range === 'custom' ? { range: r.range, from: r.from, to: r.to } : { range: r.range });
  return {
    sites: () => get(`${base()}/sites`),
    summary: (id, r) => get(`${s(id)}/summary?${rangeQs(r)}`),
    timeseries: (id, r, metric) => get(`${s(id)}/timeseries?${rangeQs(r)}&metric=${encodeURIComponent(metric)}`),
    breakdown: (id, r, dim, limit = 10) => get(`${s(id)}/breakdown/${encodeURIComponent(dim)}?${rangeQs(r)}&limit=${limit}`),
    funnel: (id, r) => get(`${s(id)}/funnel?${rangeQs(r)}`),
    realtime: (id) => get(`${s(id)}/realtime`),
    sitesTable: (r) => get(`${s('_all')}/sites-table?${rangeQs(r)}`),
    query: (id, spec) => send('POST', `${s(id)}/query`, spec),
    queryStatus: (id, qid, opts) => {
      const p = new URLSearchParams();
      if (opts?.metric) p.set('metric', opts.metric);
      if (opts?.limit) p.set('limit', String(opts.limit));
      const q = p.toString();
      return get(`${s(id)}/query/${encodeURIComponent(qid)}${q ? `?${q}` : ''}`);
    },
    exportCsv: (id, r, filters) => send('POST', `${s(id)}/export`, { ...rangeBody(r), filters }),
    exportDownload: (id, exportId) => get(`${s(id)}/export/${encodeURIComponent(exportId)}`),
    security: (id, r) => get(`${s(id)}/security?${rangeQs(r)}`),
    incidents: (id, r) => get(`${s(id)}/incidents?${rangeQs(r)}`),
    blocks: (id) => get(`${s(id)}/blocks`),
    createBlock: async (id, body) => (await send<{ block: Block }>('POST', `${s(id)}/blocks`, body)).block,
    deleteBlock: async (id, blockId, scope) => {
      await send('DELETE', `${s(id)}/blocks/${encodeURIComponent(blockId)}${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`);
    },
    settings: (id) => get(`${s(id)}/settings`),
    saveSettings: (id, body) => send('PUT', `${s(id)}/settings`, body),
  };
}
